import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Transfer } from '../types';
import { format } from 'date-fns';
import { TransferTimeline } from './TransferTimeline';
import { fileUploadService } from '../services/fileUploadService';
import { organizationsService } from '../services/organizations.service';
import { toast } from 'react-toastify';
import { RootState } from '../store/store';

interface TransferDetailModalProps {
  transfer: Transfer;
  onClose: () => void;
  onEdit?: () => void;
  onRepeat?: () => void;
  onComplete?: () => void;
  canViewFinancials?: boolean;
  canViewHistory?: boolean;
}

export const TransferDetailModal: React.FC<TransferDetailModalProps> = ({ transfer, onClose, onEdit, onRepeat, onComplete, canViewFinancials = true, canViewHistory = true }) => {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.auth.user);
  const [activeTab, setActiveTab] = useState<'details' | 'history'>('details');
  const [organizationName, setOrganizationName] = useState<string>('Transfer');

  useEffect(() => {
    const orgId = transfer.organizationId || user?.organizationId;
    if (orgId) {
      organizationsService.getById(orgId)
        .then(org => setOrganizationName(org.name))
        .catch(() => {});
    }
  }, [transfer.organizationId, user?.organizationId]);

  const handleExportPdf = () => {
    const driverName = transfer.providerDriverName || transfer.assignedDriver?.fullName || '-';
    const driverPhone = transfer.providerDriverPhone || transfer.assignedDriver?.phone || '';
    const plate = transfer.providerPlate || '-';
    const dateStr = format(new Date(transfer.transferDate), 'dd.MM.yyyy');
    const timeStr = transfer.transferTime.substring(0, 5);
    const tripTypeLabel = transfer.tripType === 'arrival' ? t('transfers.arrival') : t('transfers.departure');

    const guestRows = transfer.guests && transfer.guests.length > 0
      ? transfer.guests.map(g => `
        <tr>
          <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;font-size:14px;">${t('guest.guests')}</td>
          <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-size:14px;font-weight:500;">${g.name}${g.phone ? ' - ' + g.phone : ''}</td>
        </tr>`).join('')
      : '';

    const pdfTitle = `${organizationName} - ${transfer.transferCode || 'Transfer'}`;

    const html = `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><title>${pdfTitle}</title>
<style>
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;margin:0;padding:20px;background:#fff;color:#1e293b;}
  .container{max-width:500px;margin:0 auto;border:1px solid #e2e8f0;border-radius:12px;overflow:hidden;}
  .header{background:linear-gradient(135deg,#2563eb,#1d4ed8);color:#fff;padding:24px;text-align:center;}
  .header h1{margin:0;font-size:20px;font-weight:700;}
  .header p{margin:6px 0 0;font-size:13px;opacity:0.85;}
  .body{padding:24px;}
  .code{text-align:center;margin-bottom:20px;}
  .code span{font-size:24px;font-weight:700;font-family:monospace;color:#1e293b;background:#f1f5f9;padding:8px 20px;border-radius:8px;display:inline-block;}
  table{width:100%;border-collapse:collapse;}
  td{padding:10px 12px;font-size:14px;}
  td:first-child{color:#6b7280;width:40%;}
  td:last-child{font-weight:500;}
  tr{border-bottom:1px solid #e5e7eb;}
  tr:last-child{border-bottom:none;}
  .section{margin-top:16px;padding-top:16px;border-top:2px solid #e2e8f0;}
  .section-title{font-size:13px;font-weight:600;color:#2563eb;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:8px;padding-left:12px;}
  .footer{text-align:center;padding:16px;color:#94a3b8;font-size:11px;border-top:1px solid #e2e8f0;}
  @media print{body{padding:0;} .container{border:none;box-shadow:none;}}
</style></head><body>
<div class="container">
  <div class="header">
    <h1>${organizationName}</h1>
    <p>${t('transfers.transferDetails')} — ${tripTypeLabel}</p>
  </div>
  <div class="body">
    ${transfer.transferCode ? `<div class="code"><span>#${transfer.transferCode}</span></div>` : ''}
    <table>
      <tr><td>${t('transfers.dateTime')}</td><td>${dateStr} ${timeStr}</td></tr>
      <tr><td>${t('transfers.route')}</td><td>${transfer.pickupLocation} → ${transfer.dropoffLocation}</td></tr>
      ${transfer.flightCode ? `<tr><td>${t('transfers.flightCode')}</td><td>${transfer.flightCode}</td></tr>` : ''}
      <tr><td>${t('transfers.passengerCount')}</td><td>${transfer.passengerCount || '-'}</td></tr>
    </table>
    <div class="section">
      <div class="section-title">${t('transfers.assignedTo')}</div>
      <table>
        <tr><td>${t('transfers.driverName')}</td><td>${driverName}</td></tr>
        ${driverPhone ? `<tr><td>${t('guest.phone')}</td><td>${driverPhone}</td></tr>` : ''}
        <tr><td>${t('transfers.licensePlate')}</td><td>${plate}</td></tr>
      </table>
    </div>
    <div class="section">
      <div class="section-title">${t('transfers.customer')}</div>
      <table>
        <tr><td>${t('transfers.customerName')}</td><td>${transfer.customerName}</td></tr>
        ${transfer.customerPhone ? `<tr><td>${t('transfers.customerPhone')}</td><td>${transfer.customerPhone}</td></tr>` : ''}
      </table>
    </div>
    ${guestRows ? `<div class="section"><div class="section-title">${t('guest.guests')}</div><table>${guestRows}</table></div>` : ''}
    ${transfer.specialNotes ? `<div class="section"><div class="section-title">${t('transfers.specialNotes')}</div><p style="padding:8px 12px;font-size:14px;color:#374151;margin:0;">${transfer.specialNotes}</p></div>` : ''}
  </div>
  <div class="footer">${new Date().getFullYear()} &copy; ${organizationName}</div>
</div>
<script>window.onload=function(){window.print();}</script>
</body></html>`;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
    }
  };

  const handleViewPassport = async (passportFile: string) => {
    try {
      const key = passportFile.includes('amazonaws.com')
        ? passportFile.split('.amazonaws.com/')[1]
        : passportFile;
      const signedUrl = await fileUploadService.getSignedUrl(key);
      window.open(signedUrl, '_blank');
    } catch (error) {
      console.error('Error getting signed URL:', error);
      toast.error(t('fileUpload.viewFailed'));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] sm:max-h-[95vh] flex flex-col">
        <div className="p-4 sm:p-6 border-b border-gray-200 shrink-0 bg-white">
          <div className="flex justify-between items-center gap-2 mb-4">
            <h2 className="text-base sm:text-2xl font-bold text-gray-900 truncate">
              {t('transfers.transferDetails')} {transfer.transferCode ? `#${transfer.transferCode}` : ''}
            </h2>
            <button
              onClick={onClose}
              className="shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 text-xl font-bold transition-colors"
            >
              ×
            </button>
          </div>

          {/* Tabs */}
          <div className="flex space-x-4 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('details')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'details'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('transfers.details')}
            </button>
            {canViewHistory && (
              <button
                onClick={() => setActiveTab('history')}
                className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'history'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('transfers.history')}
              </button>
            )}
          </div>
        </div>

        {activeTab === 'history' ? (
          <div className="p-4 sm:p-6 overflow-y-auto flex-1">
            <TransferTimeline transferId={transfer.id} />
          </div>
        ) : (
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 overflow-y-auto flex-1">
          {/* 1. Transfer Info */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('transfers.transferDetails')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <p className="text-sm text-gray-500">{t('transfers.id')}</p>
                <p className="text-lg font-medium text-gray-900 font-mono">
                  {transfer.transferCode || '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('transfers.dateTime')}</p>
                <p className="text-lg font-medium text-gray-900">
                  {format(new Date(transfer.transferDate), 'dd.MM.yyyy')} <span className="text-gray-600">{transfer.transferTime.substring(0, 5)}</span>
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('transfers.transferType')}</p>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                  transfer.tripType === 'arrival'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-blue-100 text-blue-800'
                }`}>
                  {transfer.tripType === 'arrival' ? t('transfers.arrival') : t('transfers.departure')}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('common.status')}</p>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(transfer.status)}`}>
                  {t(`transfers.status_${transfer.status}`, transfer.status)}
                </span>
              </div>
              <div className="sm:col-span-2">
                <p className="text-sm text-gray-500">{t('transfers.route')}</p>
                <p className="font-medium text-gray-900">{transfer.pickupLocation}</p>
                <p className="text-gray-500 text-sm">→ {transfer.dropoffLocation}</p>
              </div>
              {transfer.flightCode && (
                <div>
                  <p className="text-sm text-gray-500">{t('transfers.flightCode')}</p>
                  <p className="font-medium text-gray-900">{transfer.flightCode}</p>
                </div>
              )}
            </div>
          </div>

          {/* 2. Assignment Info */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('transfers.assignedTo')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <p className="text-sm text-gray-500">{t('transfers.driverName')}</p>
                <p className="font-medium text-gray-900">
                  {transfer.providerDriverName || transfer.assignedDriver?.fullName || '-'}
                </p>
                {(transfer.providerDriverPhone || transfer.assignedDriver?.phone) && (
                  <p className="text-sm text-gray-600">
                    {transfer.providerDriverPhone || transfer.assignedDriver?.phone}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('transfers.licensePlate')}</p>
                <p className="font-medium text-gray-900">{transfer.providerPlate || '-'}</p>
              </div>
              {transfer.assignedProvider && canViewFinancials && (
                <div>
                  <p className="text-sm text-gray-500">{t('transfers.provider')}</p>
                  <p className="font-medium text-gray-900">{transfer.assignedProvider.name}</p>
                </div>
              )}
              {transfer.vehicleType && (
                <div>
                  <p className="text-sm text-gray-500">{t('transfers.vehicleType')}</p>
                  <p className="font-medium text-gray-900">{transfer.vehicleType}</p>
                </div>
              )}
            </div>
          </div>

          {/* 3. Customer Info */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('transfers.customer')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <p className="text-sm text-gray-500">{t('transfers.customerName')}</p>
                <p className="font-medium">{transfer.customerName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('transfers.customerPhone')}</p>
                <p className="font-medium">{transfer.customerPhone || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('transfers.passengerCount')}</p>
                <p className="font-medium">{transfer.passengerCount || '-'}</p>
              </div>
            </div>
          </div>

          {/* 4. Guests */}
          {transfer.guests && transfer.guests.length > 0 && (
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('guest.guests')}</h3>
              <div className="space-y-3">
                {transfer.guests.map((guest, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <p className="font-medium text-gray-900">{guest.name}</p>
                        {guest.phone && (
                          <p className="text-sm text-gray-600">
                            <span className="text-gray-500">{t('guest.phone')}:</span> {guest.phone}
                          </p>
                        )}
                        {guest.email && (
                          <p className="text-sm text-gray-600">
                            <span className="text-gray-500">{t('guest.email')}:</span> {guest.email}
                          </p>
                        )}
                        {guest.tcPassport && (
                          <p className="text-sm text-gray-600">
                            <span className="text-gray-500">{t('guest.tcPassport')}:</span> {guest.tcPassport}
                          </p>
                        )}
                      </div>
                      {guest.passportFile && (
                        <button
                          type="button"
                          onClick={() => handleViewPassport(guest.passportFile!)}
                          className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors flex items-center gap-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          {t('guest.viewPassport')}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 5. Special Notes */}
          {transfer.specialNotes && (
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('transfers.specialNotes')}</h3>
              <p className="text-gray-700 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">
                {transfer.specialNotes}
              </p>
            </div>
          )}

          {/* 6. Financial Info - Only for managers */}
          {canViewFinancials && (transfer.customerPrice || transfer.providerPrice) && (
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('transfers.financialInfo')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {transfer.customerPrice && (
                  <div>
                    <p className="text-sm text-gray-500">{t('transfers.customerPrice')}</p>
                    <p className="font-medium text-lg text-green-600">
                      {transfer.customerPrice} {transfer.currency}
                    </p>
                  </div>
                )}
                {transfer.providerPrice && (
                  <div>
                    <p className="text-sm text-gray-500">{t('transfers.providerPrice')}</p>
                    <p className="font-medium text-lg text-orange-600">
                      {transfer.providerPrice} {transfer.currency || 'TL'}
                    </p>
                  </div>
                )}
                {transfer.meetGreetFee != null && Number(transfer.meetGreetFee) > 0 && (
                  <div>
                    <p className="text-sm text-gray-500">{t('transfers.meetGreetFee')}</p>
                    <p className="font-medium text-lg text-blue-600">
                      {transfer.meetGreetFee} {transfer.currency}
                    </p>
                  </div>
                )}
                {transfer.netProfit != null && (
                  <div>
                    <p className="text-sm text-gray-500">{t('transfers.netProfit')}</p>
                    <p className={`font-medium text-lg ${Number(transfer.netProfit) >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                      {transfer.netProfit} {transfer.currency}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        )}

        <div className="p-4 pb-8 sm:p-6 sm:pb-6 border-t border-gray-200 sticky bottom-0 bg-white">
          {/* Action buttons row — wraps neatly on mobile */}
          <div className="flex flex-wrap gap-2 sm:gap-3 sm:items-center">
            {onEdit && (
              <button
                onClick={onEdit}
                className="flex-1 sm:flex-none px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 min-h-[48px] font-medium text-sm sm:text-base"
              >
                {t('common.edit')}
              </button>
            )}
            {onRepeat && (
              <button
                onClick={onRepeat}
                className="flex-1 sm:flex-none px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 active:bg-purple-800 min-h-[48px] font-medium flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Tekrarla
              </button>
            )}
            {onComplete && transfer.status !== 'completed' && transfer.status !== 'cancelled' && transfer.providerPrice && (
              <button
                onClick={() => {
                  onComplete();
                }}
                className="flex-1 sm:flex-none px-4 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 active:bg-emerald-800 min-h-[48px] font-medium flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Tamamla
              </button>
            )}
            <button
              onClick={handleExportPdf}
              className="flex-1 sm:flex-none px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 min-h-[48px] font-medium flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="hidden sm:inline">{t('transfers.exportPdf')}</span>
              <span className="sm:hidden">PDF</span>
            </button>
            {/* Spacer pushes Close to the right on desktop */}
            <div className="hidden sm:flex flex-1" />
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 active:bg-gray-300 min-h-[48px] font-medium text-sm sm:text-base"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-purple-100 text-purple-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
}
